"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useUnifiedPositions } from "@/contexts/unified-positions-context";
import { useAccountBalances } from "@/contexts/account-balances-context";
import { useAccountSelection } from "@/contexts/account-selection-context";
import { positionSnapshotService } from "@/services/position-snapshot-service";
import {
  computeLiveNetLiq,
  sumCashBalanceForAccounts,
  sumNetLiquidityForAccounts,
  updateNetOffsetState,
  type NetOffsetState,
} from "@/utils/live-portfolio-metrics";
import type { Position } from "@/types/position.types";
import type { QuoteLike } from "@/utils/quote-resolver";
import {
  getIntentFromPositionLike,
  getPositionIntentFlags,
  isPositionIntent,
  type PositionIntent,
} from "@/lib/position-intents";

export interface PortfolioOverviewMetrics {
  totalNetLiq: number;
  netLiqDeployed: number;
  notionalValue: number;
  portfolioDelta: number;
  portfolioLeverage: number;
  dailyProfit: number;
  dailyProfitPercent: number;
  weeklyProfit: number;
  weeklyProfitPercent: number;
  weeklyPeriodDays?: number;
}

interface PortfolioOverviewContextValue {
  metrics: PortfolioOverviewMetrics;
  hasHistoricalData: boolean;
  updateMetrics: (
    nextMetrics: Partial<PortfolioOverviewMetrics>,
    options?: { hasHistoricalData?: boolean },
  ) => void;
}

const defaultMetrics: PortfolioOverviewMetrics = {
  totalNetLiq: 0,
  netLiqDeployed: 0,
  notionalValue: 0,
  portfolioDelta: 0,
  portfolioLeverage: 0,
  dailyProfit: 0,
  dailyProfitPercent: 0,
  weeklyProfit: 0,
  weeklyProfitPercent: 0,
};

const PortfolioOverviewContext =
  createContext<PortfolioOverviewContextValue | null>(null);

const compactPositionSymbol = (symbol: string): string =>
  symbol.replace(/\s+/g, "").toUpperCase();

const positionIntentLookupKeys = (
  accountNumber: string,
  positionKey: string | null | undefined,
): string[] => {
  const rawSymbol = positionKey?.trim() ?? "";
  return [
    `${accountNumber}:${rawSymbol}`,
    `${accountNumber}:${compactPositionSymbol(rawSymbol)}`,
  ];
};

export function PortfolioOverviewProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [metrics, setMetrics] = useState<PortfolioOverviewMetrics>(defaultMetrics);
  const [hasHistoricalData, setHasHistoricalData] = useState(false);
  const [positionIntents, setPositionIntents] = useState<Map<string, PositionIntent>>(
    new Map(),
  );
  const {
    positions,
    marketData,
    greeksData,
  } = useUnifiedPositions();
  const { balances } = useAccountBalances();
  const { selectedAccounts } = useAccountSelection();
  const selectedAccountNumbers = useMemo(() => {
    const numbers = selectedAccounts
      .map((account) => account.account["account-number"]?.trim())
      .filter((value): value is string => !!value && value.length > 0);
    numbers.sort();
    return numbers;
  }, [selectedAccounts]);
  const accountsKey = useMemo(
    () => selectedAccountNumbers.join(","),
    [selectedAccountNumbers],
  );
  const offsetRef = useRef<NetOffsetState | null>(null);

  useEffect(() => {
    if (selectedAccountNumbers.length === 0) {
      setPositionIntents(new Map());
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams();
    selectedAccountNumbers.forEach((account) => params.append("account", account));

    fetch(`/api/positions/intents?${params.toString()}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Failed to load position intents");
        }
        return (await response.json()) as {
          intents?: Array<{
            accountNumber: string;
            positionKey: string;
            intent: string;
          }>;
        };
      })
      .then((data) => {
        const next = new Map<string, PositionIntent>();
        for (const intent of data.intents ?? []) {
          if (!isPositionIntent(intent.intent)) continue;
          for (const key of positionIntentLookupKeys(
            intent.accountNumber,
            intent.positionKey,
          )) {
            next.set(key, intent.intent);
          }
        }
        setPositionIntents((current) =>
          next.size === 0 && current.size > 0 ? current : next,
        );
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn("[PortfolioOverview] Failed to load position intents:", error);
      });

    return () => controller.abort();
  }, [selectedAccountNumbers]);

  const updateMetrics = useCallback(
    (
      nextMetrics: Partial<PortfolioOverviewMetrics>,
      options?: { hasHistoricalData?: boolean },
    ) => {
      setMetrics((prev) => ({ ...prev, ...nextMetrics }));
      if (options?.hasHistoricalData !== undefined) {
        setHasHistoricalData(options.hasHistoricalData);
      }
    },
    [],
  );

  useEffect(() => {
    if (selectedAccountNumbers.length === 0) {
      offsetRef.current = null;
      setMetrics((prev) => ({
        ...prev,
        totalNetLiq: 0,
        netLiqDeployed: 0,
        notionalValue: 0,
        portfolioDelta: 0,
        portfolioLeverage: 0,
      }));
      return;
    }

    const positionsForAccounts = positions.filter((position) => {
      const accountNumber =
        position["account-number"]?.toString().trim() ??
        (position as { accountNumber?: string }).accountNumber?.trim() ??
        "";
      return accountNumber ? selectedAccountNumbers.includes(accountNumber) : false;
    }) as Position[];

    const activePositionsForAccounts = positionsForAccounts.filter((position) => {
      const accountNumber = position["account-number"]?.toString().trim() ?? "";
      const intent =
        positionIntentLookupKeys(accountNumber, position.symbol)
          .map((key) => positionIntents.get(key))
          .find((value): value is PositionIntent => value !== undefined) ??
        getIntentFromPositionLike(position as unknown as Record<string, unknown>);
      return getPositionIntentFlags(intent).includeInActiveAllocation;
    });

    const quoteMap = marketData as unknown as Map<string, QuoteLike>;
    const greeksMap = greeksData as unknown as Map<string, Record<string, unknown>>;

    const allPositionsSnapshot = positionSnapshotService.buildSnapshot({
      positions: positionsForAccounts,
      quotes: quoteMap,
      greeks: greeksMap,
    });
    const activeSnapshot = positionSnapshotService.buildSnapshot({
      positions: activePositionsForAccounts,
      quotes: quoteMap,
      greeks: greeksMap,
    });

    const aggregateNetLiq = sumNetLiquidityForAccounts(
      balances,
      selectedAccountNumbers,
    );
    const cashTotal = sumCashBalanceForAccounts(
      balances,
      selectedAccountNumbers,
    );

    const rawNetLiq = allPositionsSnapshot.totals.marketValue + cashTotal;

    offsetRef.current = updateNetOffsetState(
      offsetRef.current,
      accountsKey,
      aggregateNetLiq,
      rawNetLiq,
    );

    const liveNetLiq = computeLiveNetLiq(
      rawNetLiq,
      offsetRef.current,
    );

    const leverageDenominator =
      liveNetLiq !== 0
        ? Math.abs(liveNetLiq)
        : Math.abs(activeSnapshot.totals.marketValue);
    const nextLeverage =
      leverageDenominator > 0
        ? Math.abs(activeSnapshot.totals.notional) / leverageDenominator
        : 0;

    setMetrics((prev) => {
      const next = {
        ...prev,
        totalNetLiq: liveNetLiq,
        netLiqDeployed: activeSnapshot.totals.marketValue,
        notionalValue: Math.abs(activeSnapshot.totals.notional),
        portfolioDelta: activeSnapshot.totals.positionDelta,
        portfolioLeverage: nextLeverage,
      };

      const changed =
        Math.abs(next.totalNetLiq - prev.totalNetLiq) > 0.5 ||
        Math.abs(next.netLiqDeployed - prev.netLiqDeployed) > 0.5 ||
        Math.abs(next.notionalValue - prev.notionalValue) > 0.5 ||
        Math.abs(next.portfolioDelta - prev.portfolioDelta) > 0.05 ||
        Math.abs(next.portfolioLeverage - prev.portfolioLeverage) > 0.005;

      return changed ? next : prev;
    });
  }, [
    accountsKey,
    balances,
    greeksData,
    marketData,
    positions,
    positionIntents,
    selectedAccountNumbers,
  ]);

  const value = useMemo<PortfolioOverviewContextValue>(
    () => ({ metrics, hasHistoricalData, updateMetrics }),
    [metrics, hasHistoricalData, updateMetrics],
  );

  return (
    <PortfolioOverviewContext.Provider value={value}>
      {children}
    </PortfolioOverviewContext.Provider>
  );
}

export function usePortfolioOverview() {
  const context = useContext(PortfolioOverviewContext);
  if (!context) {
    throw new Error(
      "usePortfolioOverview must be used within a PortfolioOverviewProvider",
    );
  }
  return context;
}

export { defaultMetrics as DEFAULT_PORTFOLIO_OVERVIEW_METRICS };
